import React from 'react';
// import Button from '@common_button';
import TextField from '@common_forms/TextField';
import { regexPhone, regexSpecialChar } from '@helper_regex';
import { useTranslation } from 'next-i18next';
import Dialog from '@common_dialog';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import gqlService from '@core_modules/checkout/services/graphql';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectCheckoutState, setCheckoutData, setErrorState, setPickupInformation, setSelectedData, setLoading, setStatusState, setPickupLocationCode,
} from '@core_modules/checkout/redux/checkoutSlice';
import { filterSpecialChars } from '@helpers/formInput';
import AddressForm from '@core_modules/checkout/plugins/AddressForm';
import _ from 'lodash';
import Checkbox from '@common_forms/CheckBox';
import Typography from '@common_typography';
import Button from '@common_button';
import cx from 'classnames';
import { getLoginInfo } from '@helper_auth';

const CLOSE_ADDRESS_DIALOG = 100;

const ModalPickupInformation = (props) => {
    const {
        open, setOpen, isOnlyVirtualProductOnCart, refetchDataCart, refetchItemCart, updateFormik, checkoutTokenState, setCheckoutTokenState,
    } = props;

    const dispatch = useDispatch();
    const checkout = useSelector(selectCheckoutState);

    const { t } = useTranslation(['common', 'checkout', 'validate']);
    const [setPickupStore] = gqlService.setPickupStore();
    const [setShippingAddressById] = gqlService.setShippingAddress();
    const [setBillingAddressById] = gqlService.setBillingAddressById();
    const [setBillingAddressByInput] = gqlService.setBillingAddressOnCartByInput();
    const [setDefaultAddress] = gqlService.updatedDefaultAddress();
    const [setBillingAddressVirtualProduct] = gqlService.setBillingAddressVirtualProduct();

    const isLogin = getLoginInfo();
    const [useDefaultBillingAddress, setIsUseDefaultBillingAddress] = React.useState(!!isLogin);

    const { billing } = checkout.selected;
    const { data } = checkout;

    const validationSchema = Yup.object().shape({
        email: Yup.string().email(t('validate:email:wrong')).required(t('validate:email:required')),
        person: Yup.string().required(`${t('checkout:pickupInformation:pickupPerson')} ${t('validate:required')}`)
            .matches(regexSpecialChar, t('common:validate:specialChar')),
        phoneNumber: Yup.string().required(t('validate:phoneNumber:required')).matches(regexPhone, t('validate:phoneNumber:wrong')),
    });

    const toggleCheckbox = () => {
        setIsUseDefaultBillingAddress(!useDefaultBillingAddress);
    };

    // eslint-disable-next-line no-unused-vars
    const [loadingButton, setLoadingButton] = React.useState(false);
    const formik = useFormik({
        initialValues: {
            email: checkout.pickupInformation.pickup_person_email || '',
            phoneNumber: checkout.pickupInformation.pickup_person_phone || '',
            person: checkout.pickupInformation.pickup_person_name || '',
        },
        validationSchema,
        onSubmit: async (values) => {
            const pickupInformation = {
                pickup_person_email: values.email,
                pickup_person_name: filterSpecialChars(values?.person),
                pickup_person_phone: values.phoneNumber,
            };
            await setLoadingButton(true);
            if (Object.keys(checkout.selectStore).length > 0) {
                await setPickupStore({
                    variables: {
                        cart_id: checkout.data.cart.id,
                        code: checkout.selectStore.code,
                        extension_attributes: pickupInformation,
                        store_address: {
                            city: checkout.selectStore.city,
                            country_code: checkout.selectStore.country_id,
                            name: checkout.selectStore.name,
                            postcode: checkout.selectStore.postcode,
                            region: checkout.selectStore.region,
                            street: [checkout.selectStore.street],
                            telephone: checkout.selectStore.telephone,
                        },
                    },
                }).then(async (res) => {
                    const paymentMethod = res.data.setPickupStore.available_payment_methods.map((method) => ({
                        ...method,
                        label: method.title,
                        value: method.code,
                        image: null,
                    }));

                    dispatch(setCheckoutData({
                        cart: {
                            ...checkout.data.cart,
                            ...res.data.setPickupStore,
                        },
                        paymentMethod,
                    }));

                    dispatch(setPickupInformation(pickupInformation));
                    dispatch(setErrorState({ selectStore: false, pickupInformation: false }));
                    await setLoadingButton(false);
                    // setOpen();
                }).catch((err) => {
                    window.toastMessage({
                        open: true,
                        variant: 'error',
                        text: err.message.split(':')[1] || t('checkout:message:serverError'),
                    });
                    setLoadingButton(false);
                });
            } else {
                dispatch(setPickupInformation(pickupInformation));
                dispatch(setErrorState({ selectStore: true }));
                await setLoadingButton(false);
                // setOpen();
            }
        },
    });

    const updateAddressState = async (result) => {
        const updatedCart = await result.data.setBillingAddressOnCart.cart;
        if (isOnlyVirtualProductOnCart) {
            dispatch(
                setSelectedData({
                    billing: updatedCart?.billing_address,
                    address: updatedCart?.billing_address,
                }),
            );
        } else {
            const [shippingAddress] = updatedCart.shipping_addresses;
            if (shippingAddress && data.isGuest) {
                dispatch(
                    setSelectedData({
                        address: shippingAddress,
                    }),
                );
            }

            const billingAddress = await updatedCart.billing_address;
            if (billingAddress && data.isGuest) {
                dispatch(
                    setSelectedData({
                        biling: billingAddress,
                    }),
                );
            }

            if (checkout.selected.delivery === 'home' && typeof shippingAddress.is_valid_city !== 'undefined') {
                dispatch(
                    setErrorState({
                        shippingAddress: !shippingAddress.is_valid_city,
                    }),
                );
            }
        }
        dispatch(setLoading({ addresses: false }));
        const mergeCart = {
            ...checkout.data.cart,
            ...updatedCart,
        };
        dispatch(
            setCheckoutData({
                cart: mergeCart,
            }),
        );

        if (refetchDataCart && typeof refetchDataCart() === 'function') {
            refetchDataCart();
        }
        if (refetchItemCart && typeof refetchItemCart() === 'function') {
            refetchItemCart();
        }
        updateFormik(mergeCart);
    };

    const setAddress = (selectedAddress, cart, firstLoad = false) =>
        new Promise((resolve, reject) => {
            if (checkout.data.isGuest) {
                dispatch(setLoading({ addresses: true }));
            }
            const { latitude, longitude } = selectedAddress;

            if (checkout.data.isGuest) {
                if (isOnlyVirtualProductOnCart) {
                    setBillingAddressVirtualProduct({
                        variables: {
                            cartId: cart.id,
                            ...selectedAddress,
                            latitude,
                            longitude,
                        },
                    })
                        .then((resBilling) => {
                            updateAddressState(resBilling);
                            resolve();
                        })
                        .catch((e) => {
                            if (e.message.includes('Token is wrong.')) {
                                setCheckoutTokenState(!checkoutTokenState);
                            } else {
                                reject(e);
                            }
                        });
                } else {
                    setBillingAddressByInput({
                        variables: {
                            cartId: cart.id,
                            ...selectedAddress,
                            latitude,
                            longitude,
                            same_as_shipping: false,
                        },
                    })
                        .then(async (resBilling) => {
                            updateAddressState(resBilling);
                            resolve();
                        })
                        .catch((e) => {
                            if (e.message.includes('Token is wrong.')) {
                                setCheckoutTokenState(!checkoutTokenState);
                            } else {
                                reject(e);
                            }
                        });
                }
            } else if (isOnlyVirtualProductOnCart) {
                setBillingAddressById({
                    variables: {
                        cartId: cart.id,
                        addressId: selectedAddress.id,
                    },
                })
                    .then((resBilling) => {
                        updateAddressState(resBilling);
                        resolve();
                    })
                    .catch((e) => {
                        if (e.message.includes('Token is wrong.')) {
                            setCheckoutTokenState(!checkoutTokenState);
                        } else {
                            reject(e);
                        }
                    });
            } else if (checkout?.selected?.delivery === 'pickup') {
                setBillingAddressByInput({
                    variables: {
                        cartId: cart.id,
                        ...selectedAddress,
                        latitude,
                        longitude,
                        same_as_shipping: false,
                    },
                })
                    .then(async (resBilling) => {
                        updateAddressState(resBilling);
                        resolve();
                    })
                    .catch((e) => {
                        if (e.message.includes('Token is wrong.')) {
                            setCheckoutTokenState(!checkoutTokenState);
                        } else {
                            reject(e);
                        }
                    });
            } else {
                const setShippingBilling = () => {
                    dispatch(setLoading({ shipping: true }));
                    setShippingAddressById({
                        variables: {
                            cartId: cart.id,
                            addressId: selectedAddress.id,
                        },
                    })
                        .then((resBilling) => {
                            updateAddressState(resBilling);
                            resolve();
                            dispatch(setLoading({ shipping: false }));
                        })
                        .catch((e) => {
                            dispatch(setLoading({ shipping: false }));
                            if (e.message.includes('Token is wrong.')) {
                                setCheckoutTokenState(!checkoutTokenState);
                            } else {
                                reject(e);
                            }
                        });
                };
                if (firstLoad) {
                    dispatch(setLoading({ addresses: true }));
                    setDefaultAddress({
                        variables: {
                            addressId: selectedAddress.id,
                            street: selectedAddress.street[0],
                        },
                    })
                        .then((dataAddress) => {
                            if (dataAddress && dataAddress.data && dataAddress.data.updateCustomerAddress) {
                                const shipping = dataAddress.data.updateCustomerAddress;
                                dispatch(
                                    setSelectedData({
                                        address: {
                                            firstname: shipping.firstname,
                                            lastname: shipping.lastname,
                                            city: shipping.city,
                                            region: {
                                                ...shipping.region,
                                                label: shipping.region.region,
                                            },
                                            country: shipping.country,
                                            postcode: shipping.postcode,
                                            telephone: shipping.telephone,
                                            street: shipping.street,
                                        },
                                    }),
                                );
                                dispatch(setLoading({ order: false, addresses: false }));
                            }
                            setShippingBilling();
                        })
                        .catch((e) => {
                            reject(e);
                        });
                } else {
                    setShippingBilling();
                }
            }
        });

    const street = billing?.street?.join(' ') ?? null;

    const addressFormProps = billing
        ? {
            region: billing.region.label,
            country: {
                id: billing?.country.code,
                full_name_locale: billing?.country?.label === 'ID' ? 'Indonesia' : billing?.country?.label,
            },
            city: billing.city,
            street,
            firstname: billing.firstname,
            lastname: billing.lastname,
            postcode: billing.postcode,
            telephone: billing.telephone,
        }
        : {};

    return (
        <Dialog
            open={open}
            closeOnBackdrop
            onClose={() => setOpen()}
            onClickCloseTitle={() => setOpen()}
            title={t('checkout:pickupInformation:label')}
            useCloseTitleButton
            content={(
                <>
                    <div
                        // onSubmit={formik.handleSubmit}
                        className={cx(
                            'flex flex-col gap-4 overflow-y-auto',
                        )}
                        style={{ height: useDefaultBillingAddress ? 'auto' : '75vh' }}
                    >
                        <div className="flex flex-col gap-4 py-4 border-t border-neutral-200">
                            <TextField
                                label={t('checkout:pickupInformation:pickupPerson')}
                                name="person"
                                className="w-full"
                                value={formik.values.person}
                                onChange={formik.handleChange}
                                hintProps={{
                                    displayHintText: !!((formik.touched.person && formik.errors.person)),
                                    hintText: formik.errors.person,
                                    hintType: 'error',
                                }}
                                absolute={false}
                            />
                            <TextField
                                label={t('common:form:phoneNumber')}
                                name="phoneNumber"
                                className="w-full"
                                value={formik.values.phoneNumber}
                                onChange={formik.handleChange}
                                hintProps={{
                                    displayHintText: !!((formik.touched.phoneNumber && formik.errors.phoneNumber)),
                                    hintText: formik.errors.phoneNumber,
                                    hintType: 'error',
                                }}
                                absolute={false}
                            />
                            <TextField
                                label="email"
                                name="email"
                                className="w-full"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                hintProps={{
                                    displayHintText: !!((formik.touched.email && formik.errors.email)),
                                    hintText: formik.errors.email,
                                    hintType: 'error',
                                }}
                                absolute={false}
                            />
                        </div>

                        {isLogin ? (
                            <Checkbox
                                id="checkbox-use-default-billing-address"
                                variant="single"
                                label={t('common:useDefaultBillingAddress')}
                                checked={useDefaultBillingAddress}
                                name="useDefaultBillingAddress"
                                onChange={() => {
                                    toggleCheckbox();
                                }}
                                classNames={{
                                    checkboxContainerClasses: cx('flex', 'flex-row'),
                                    checkboxClasses: cx('cursor-pointer', 'min-w-5', 'focus:shadow-none'),
                                }}
                            >
                                <label className="mt-[-2px]">
                                    <Typography>{t('common:useDefaultBillingAddress')}</Typography>
                                </label>
                            </Checkbox>
                        ) : null}

                        {!useDefaultBillingAddress ? (
                            <>
                                <div
                                    className={cx(
                                        'font-semibold text-[16px] text-neutral-700 mt-4',
                                    )}
                                >
                                    {t('common:billingAddress')}
                                </div>
                                <AddressForm
                                    t={t}
                                    open={open}
                                    onSubmitAddress={async (dataAddress) => {
                                        window.backdropLoader(true);
                                        const { cart } = checkout.data;

                                        await setAddress(dataAddress, cart);
                                        dispatch(setStatusState({ addresses: true }));
                                        dispatch(setPickupLocationCode({ addresses: null }));

                                        _.delay(async () => {
                                            dispatch(setStatusState({ addresses: false }));
                                            await setAddress(dataAddress, cart);
                                            setOpen(false);
                                            window.backdropLoader(false);
                                        }, CLOSE_ADDRESS_DIALOG);
                                    }}
                                    formikPickerInfo={formik}
                                    {...addressFormProps}
                                />
                            </>
                        ) : null}

                        {(isLogin && useDefaultBillingAddress) ? (

                            <Button
                                loading={loadingButton}
                                className=""
                                type="submit"
                                classNameText="justify-center"
                                onClick={async () => {
                                    await formik.submitForm();
                                    await setOpen();
                                }}
                            >
                                {t('common:button:save')}
                            </Button>
                        ) : null}
                    </div>
                </>
            )}
        />
    );
};

export default ModalPickupInformation;
